/**
 * Composable for managing dynamic z-index values based on component context
 * This ensures proper layering of components like dropdowns, modals, etc.
 */

// Global z-index counter for managing multiple open dropdowns
let globalZIndexCounter = 1000
const openDropdowns = new Set<number>()

// Track open modals
let openModalsCount = 0

export const useZIndex = () => {
  // Base z-index values for different component types
  const zIndexValues = {
    // Base components
    dropdown: 10,
    tooltip: 20,
    popover: 30,

    // Navigation components
    navbar: 1100,
    header: 1100,
    sidebar: 85,
    mobileNav: 1100,

    // Modal components - updated to match Modal component
    modal: 9999,
    modalContent: 10000,

    // Map components
    map: 50,
    mapControls: 60,
  }

  /**
   * Get the appropriate z-index for a dropdown/select component
   * @param isInModal - Whether the component is inside a modal
   * @param isInSidebar - Whether the component is inside a sidebar
   * @returns The appropriate z-index value
   */
  const getDropdownZIndex = (isInModal = false, isInSidebar = false) => {
    if (isInModal) {
      // If in modal, should be above modal content but below modal backdrop
      return zIndexValues.modalContent + 10 // 120
    }

    if (isInSidebar) {
      // If in sidebar, should be above sidebar but below header/navbar
      return zIndexValues.sidebar + 10 // 95
    }

    // Default: should be above body content but below navigation
    return zIndexValues.dropdown + 50 // 60
  }

  /**
   * Get the appropriate z-index for a date picker component
   * @param isInModal - Whether the component is inside a modal
   * @param isInSidebar - Whether the component is inside a sidebar
   * @returns The appropriate z-index value
   */
  const getDatePickerZIndex = (isInModal = false, isInSidebar = false) => {
    // Date picker uses the same logic as dropdown
    return getDropdownZIndex(isInModal, isInSidebar)
  }

  /**
   * Check if a component is inside a modal by traversing up the DOM tree
   * @param element - The DOM element to check
   * @returns Boolean indicating if element is inside a modal
   */
  const isInModal = (element: HTMLElement | null): boolean => {
    if (!element || typeof document === "undefined") return false

    let current: HTMLElement | null = element
    let depth = 0
    const maxDepth = 20 // Prevent infinite loops

    while (current && depth < maxDepth) {
      // Check for modal classes or data attributes
      if (current.classList.contains("modal") || current.hasAttribute("data-modal")) {
        return true
      }

      // Check for high z-index that indicates modal (9999+)
      try {
        const zIndex = window.getComputedStyle(current).zIndex
        if (zIndex && !isNaN(parseInt(zIndex)) && parseInt(zIndex) >= 9999) {
          // Likely inside a modal
          return true
        }
      } catch (e) {
        // Ignore errors from getComputedStyle
      }

      // Check if current element is a modal container
      if (current.classList.contains("fixed") && current.classList.contains("inset-0")) {
        const zIndex = window.getComputedStyle(current).zIndex
        if (zIndex && parseInt(zIndex) >= 9999) {
          return true
        }
      }

      current = current.parentElement
      depth++
    }
    return false
  }

  /**
   * Check if a component is inside a sidebar by traversing up the DOM tree
   * @param element - The DOM element to check
   * @returns Boolean indicating if element is inside a sidebar
   */
  const isInSidebar = (element: HTMLElement | null): boolean => {
    if (!element) return false

    let current = element.parentElement
    while (current) {
      // Check for sidebar classes
      if (current.classList.contains("sidebar") || current.hasAttribute("data-sidebar") || current.id === "sidebar") {
        return true
      }
      current = current.parentElement
    }
    return false
  }

  /**
   * Get dynamic z-index for a component based on its DOM context
   * @param element - The DOM element to check context for
   * @param componentType - Type of component ('dropdown' | 'datepicker')
   * @returns The appropriate z-index value
   */
  const getDynamicZIndex = (element: HTMLElement | null, componentType: "dropdown" | "datepicker" = "dropdown") => {
    const inModal = isInModal(element)
    const inSidebar = isInSidebar(element)

    if (componentType === "datepicker") {
      return getDatePickerZIndex(inModal, inSidebar)
    }

    return getDropdownZIndex(inModal, inSidebar)
  }

  /**
   * Check if there's an open modal on the page
   * @returns Boolean indicating if a modal is open
   */
  const hasOpenModal = (): boolean => {
    if (typeof document === "undefined") return false

    // First check the counter (most reliable)
    if (openModalsCount > 0) return true

    // Also check DOM for modal elements with high z-index (9999+)
    // Check for elements with fixed positioning and high z-index
    const fixedElements = document.querySelectorAll(".fixed.inset-0")
    for (let i = 0; i < fixedElements.length; i++) {
      const el = fixedElements[i] as HTMLElement
      try {
        const zIndex = window.getComputedStyle(el).zIndex
        if (zIndex && !isNaN(parseInt(zIndex)) && parseInt(zIndex) >= 9999) {
          return true
        }
      } catch (e) {
        // Ignore errors
      }
    }

    return false
  }

  /**
   * Get a unique, incrementing z-index for dropdowns to ensure proper stacking
   * Each dropdown that opens gets a higher z-index than the previous one
   * @param isInsideModal - Whether the dropdown is inside a modal
   * @returns A unique z-index value
   */
  const getNextDropdownZIndex = (isInsideModal: boolean = false): number => {
    const modalOpen = hasOpenModal()

    // If there's an open modal and dropdown is NOT inside it, cap z-index below modal
    if (modalOpen && !isInsideModal) {
      // Use a lower z-index range (50-99) for dropdowns outside modals when modal is open
      // Reset counter to ensure we stay in this range
      if (globalZIndexCounter >= 99 || globalZIndexCounter < 50) {
        globalZIndexCounter = 49 // Will become 50 on next increment
      }
      globalZIndexCounter += 1
      openDropdowns.add(globalZIndexCounter)
      return globalZIndexCounter
    }

    // If inside modal, use z-index above modal content
    if (isInsideModal) {
      // Start from modal content z-index + 1 and increment
      const modalBaseZ = zIndexValues.modalContent + 1 // 10001
      if (globalZIndexCounter < modalBaseZ) {
        globalZIndexCounter = modalBaseZ - 1 // Will become modalBaseZ on next increment
      }
      globalZIndexCounter += 1
      openDropdowns.add(globalZIndexCounter)
      return globalZIndexCounter
    }

    // Normal case: no modal open, use regular stacking starting from 1000
    if (globalZIndexCounter < 1000) {
      globalZIndexCounter = 999 // Will become 1000 on next increment
    }
    globalZIndexCounter += 1
    openDropdowns.add(globalZIndexCounter)
    return globalZIndexCounter
  }

  /**
   * Release a z-index when dropdown closes (optional cleanup)
   * @param zIndex - The z-index to release
   */
  const releaseDropdownZIndex = (zIndex: number): void => {
    openDropdowns.delete(zIndex)
  }

  /**
   * Get dynamic z-index with stacking support for multiple open dropdowns/datepickers
   * @param element - The DOM element to check context for
   * @param componentType - Type of component ('dropdown' | 'datepicker')
   * @param useStacking - Whether to use stacking z-index (default: true for dropdowns and datepickers)
   * @returns The appropriate z-index value
   */
  const getDynamicZIndexWithStacking = (element: HTMLElement | null, componentType: "dropdown" | "datepicker" = "dropdown", useStacking: boolean = true): number => {
    const baseZIndex = getDynamicZIndex(element, componentType)
    const inModal = isInModal(element)

    if (useStacking) {
      // Use stacking z-index for both dropdowns and datepickers to ensure proper layering
      // Pass isInsideModal flag to ensure proper z-index relative to modals
      return getNextDropdownZIndex(inModal)
    }

    return baseZIndex
  }

  /**
   * Register that a modal has opened (for z-index management)
   */
  const registerModalOpen = (): void => {
    openModalsCount += 1
  }

  /**
   * Register that a modal has closed (for z-index management)
   */
  const registerModalClose = (): void => {
    openModalsCount = Math.max(0, openModalsCount - 1)
  }

  return {
    zIndexValues,
    getDropdownZIndex,
    getDatePickerZIndex,
    isInModal,
    isInSidebar,
    getDynamicZIndex,
    getNextDropdownZIndex,
    releaseDropdownZIndex,
    getDynamicZIndexWithStacking,
    hasOpenModal,
    registerModalOpen,
    registerModalClose,
  }
}
